export interface TourRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type TourCardSide = "top" | "bottom" | "left" | "right";

export interface TourCardPlacement {
  top: number;
  left: number;
  width: number;
  side: TourCardSide;
}

const GAP = 28;
const MARGIN = 16;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function overlaps(a: TourRect, b: TourRect, pad = 10): boolean {
  return !(
    a.left + a.width + pad <= b.left ||
    b.left + b.width + pad <= a.left ||
    a.top + a.height + pad <= b.top ||
    b.top + b.height + pad <= a.top
  );
}

function insideViewport(
  top: number,
  left: number,
  width: number,
  height: number,
  viewport: { width: number; height: number },
): boolean {
  return (
    top >= MARGIN - 1 &&
    left >= MARGIN - 1 &&
    top + height <= viewport.height - MARGIN + 1 &&
    left + width <= viewport.width - MARGIN + 1
  );
}

/**
 * Legt die Sprechblase neben das markierte Ziel, ohne es zu verdecken.
 * Unten und oben haben Vorrang, damit die Karte zur Schaltfläche gehört.
 */
export function placeTourCard(
  target: TourRect,
  cardHeight: number,
  viewport: { width: number; height: number },
  preferredWidth = 340,
): TourCardPlacement {
  const width = Math.min(preferredWidth, Math.max(220, viewport.width - MARGIN * 2));
  const height = Math.min(cardHeight, Math.max(120, viewport.height - MARGIN * 2));
  const leftAligned = clamp(target.left, MARGIN, viewport.width - width - MARGIN);
  const topAligned = clamp(target.top, MARGIN, viewport.height - height - MARGIN);

  const spaceAbove = target.top - MARGIN;
  const spaceBelow = viewport.height - (target.top + target.height) - MARGIN;
  const spaceLeft = target.left - MARGIN;
  const spaceRight = viewport.width - (target.left + target.width) - MARGIN;

  const candidates: Array<{ side: TourCardSide; top: number; left: number; space: number }> = [
    { side: "bottom", top: target.top + target.height + GAP, left: leftAligned, space: spaceBelow },
    { side: "top", top: target.top - GAP - height, left: leftAligned, space: spaceAbove },
    { side: "right", top: topAligned, left: target.left + target.width + GAP, space: spaceRight },
    { side: "left", top: topAligned, left: target.left - GAP - width, space: spaceLeft },
  ];

  const fitting = candidates.filter((item) => {
    if (!insideViewport(item.top, item.left, width, height, viewport)) return false;
    return !overlaps({ top: item.top, left: item.left, width, height }, target);
  });

  const vertical = fitting.filter((item) => item.side === "top" || item.side === "bottom");
  vertical.sort((a, b) => b.space - a.space);
  const chosen = vertical[0] ?? [...fitting].sort((a, b) => b.space - a.space)[0];
  if (chosen) return { top: chosen.top, left: chosen.left, width, side: chosen.side };

  const ranked = [...candidates].sort((a, b) => b.space - a.space);
  const fallback = ranked[0] ?? candidates[1]!;
  const top = clamp(fallback.top, MARGIN, viewport.height - height - MARGIN);
  const left = clamp(fallback.left, MARGIN, viewport.width - width - MARGIN);
  if (overlaps({ top, left, width, height }, target)) {
    const corners = [
      { top: MARGIN, left: MARGIN },
      { top: MARGIN, left: viewport.width - width - MARGIN },
      { top: viewport.height - height - MARGIN, left: MARGIN },
      { top: viewport.height - height - MARGIN, left: viewport.width - width - MARGIN },
    ];
    const centerX = target.left + target.width / 2;
    const centerY = target.top + target.height / 2;
    corners.sort((a, b) => {
      const dist = (point: { top: number; left: number }) =>
        Math.hypot(point.left + width / 2 - centerX, point.top + height / 2 - centerY);
      return dist(b) - dist(a);
    });
    for (const corner of corners) {
      if (!overlaps({ ...corner, width, height }, target)) {
        return { top: corner.top, left: corner.left, width, side: fallback.side };
      }
    }
  }
  return { top, left, width, side: fallback.side };
}
